import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Tabs, TabsContent, TabsCount, TabsList, TabsTrigger } from "../ui/tabs";
import {
  WorkbenchPage,
  WorkbenchPageContent,
  WorkbenchPageHeader,
  WorkbenchPageToolbar,
} from "../ui/workbench-page";
import { Badge } from "../ui/badge";
import { SectionCard } from "../ui/section-card";
import { cn } from "../../lib/utils";
import { EmptyState } from "../ui/empty-state";
import { Spinner } from "../ui/spinner";
import { ScoreBadge, ScoreCard } from "../ui/score-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { LlmModelSelect } from "./LlmModelSelect";
import type { Chapter } from "../../types";
import type {
  DesktopStoryCraftFeedback,
  StoryCraftAssessment,
  StoryCraftChecklistItem,
} from "../../../shared/storyCraftFeedback";
import { averageStoryCraftScore } from "../../../shared/storyCraftFeedback";
import {
  EditorScoreForm,
  editorFeedbackFromChapterFields,
  type DesktopEditorFeedback,
} from "./EditorScoreForm";
import { ImproveThisStrip } from "./ImproveThisStrip";
import { LearnPanel } from "./LearnPanel";
import type { StoryCraftAreaId } from "../../../shared/craftAreas";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import { getWebApiBaseUrl } from "../../lib/runtime";

function scoreLabel(avg: number | null): string {
  if (avg == null) return "";
  if (avg >= 5) return "Excellent";
  if (avg >= 4) return "Good";
  if (avg >= 3) return "Fair";
  if (avg >= 2) return "Needs work";
  return "Poor";
}

function displayedSnapshot(feedback: DesktopStoryCraftFeedback): string | null {
  const sc = feedback.summary?.trim();
  if (sc && sc !== "Analyzing…" && !sc.startsWith("Analysis failed")) return sc;
  return null;
}

const PROMISE_TYPE_TONE: Record<string, string> = {
  foreshadowing: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  question: "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  setup: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  tension: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  mystery: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
};

function promiseTypeTone(type: string): string {
  const t = type.trim().toLowerCase().replace(/\s+/g, "-") || "setup";
  return PROMISE_TYPE_TONE[t] ?? PROMISE_TYPE_TONE.setup;
}

const ASSESSMENT_ROWS: { key: keyof Omit<StoryCraftAssessment, "overallNotes">; label: string }[] = [
  { key: "plotProgression", label: "Plot Progression" },
  { key: "characterDevelopment", label: "Character Development" },
  { key: "themeReinforcement", label: "Theme Reinforcement" },
  { key: "pacing", label: "Pacing" },
  { key: "conflictTension", label: "Conflict & Tension" },
  { key: "hookEnding", label: "Hook & Ending" },
];

function StoryCraftChecklistItemRow({
  item,
  busy,
  onToggle,
  onUpdate,
  onDelete,
}: {
  item: StoryCraftChecklistItem;
  busy: boolean;
  onToggle: (completed: boolean) => void;
  onUpdate: (patch: { category?: string; suggestion?: string }) => void;
  onDelete: () => void;
}) {
  const [category, setCategory] = useState(item.category);
  const [suggestion, setSuggestion] = useState(item.suggestion);

  useEffect(() => {
    setCategory(item.category);
    setSuggestion(item.suggestion);
  }, [item.id, item.category, item.suggestion]);

  function commitEdits(): void {
    const nextCategory = category.trim() || "general";
    const nextSuggestion = suggestion.trim();
    if (nextCategory !== item.category || nextSuggestion !== item.suggestion) {
      onUpdate({ category: nextCategory, suggestion: nextSuggestion });
    }
  }

  // A checklist is a list, not a form: the suggestion leads, category and date
  // are metadata beneath it, and both fields stay chromeless until hover/focus.
  const quietField =
    "border-transparent bg-transparent shadow-none group-hover/row:border-border/60 focus-visible:border-ring focus-visible:bg-card";

  return (
    <div
      className={cn(
        "group/row flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-muted/30",
        item.isCompleted && "opacity-80"
      )}
    >
      <Checkbox
        className="mt-1 shrink-0"
        checked={item.isCompleted}
        onCheckedChange={(checked) => onToggle(checked === true)}
        disabled={busy}
        aria-label={`Mark checklist item ${item.isCompleted ? "incomplete" : "complete"}`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Textarea
          className={cn(
            "min-h-6 resize-none px-1 py-0.5 text-sm leading-normal field-sizing-content",
            quietField,
            item.isCompleted && "text-muted-foreground line-through"
          )}
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
          onBlur={commitEdits}
          disabled={busy}
          aria-label="Checklist suggestion"
          placeholder="Describe the improvement…"
          rows={1}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className={cn(
              "h-5.5 w-32 px-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground",
              quietField
            )}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onBlur={commitEdits}
            disabled={busy}
            aria-label="Checklist category"
            placeholder="Category"
          />
          {item.isCompleted && item.completedAt ? (
            <span className="text-[0.6875rem] whitespace-nowrap text-emerald-600 dark:text-emerald-400">
              Done {new Date(item.completedAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>
      </div>
      {/* Hidden until approach, but still reachable by keyboard. */}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-6 shrink-0 px-1.5 text-[0.6875rem] text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100"
        onClick={onDelete}
        disabled={busy}
        aria-label="Remove checklist item"
      >
        Remove
      </Button>
    </div>
  );
}

export function StoryCraftChapterPanel({
  activeChapter,
  feedback,
  busy,
  storyCraftModel,
  onStoryCraftModelChange,
  chatModelsOpenAi,
  chatModelsAnthropic,
  onRunStoryCraft,
  onDeleteStoryCraft,
  onToggleChecklistItem,
  onUpdateChecklistItem,
  onAddChecklistItem,
  onDeleteChecklistItem,
  onOpenManuscript,
  onGenerateVariationFromChecklist,
  variationBusy = false,
  showEditorScore = false,
  editorFeedback = null,
  canEditEditorScore = false,
  onSaveEditorScore,
}: {
  activeChapter: Chapter;
  feedback: DesktopStoryCraftFeedback | null;
  busy: boolean;
  storyCraftModel: string;
  onStoryCraftModelChange: (model: string, provider: "openai" | "anthropic") => void;
  chatModelsOpenAi: readonly { value: string; label: string }[];
  chatModelsAnthropic: readonly { value: string; label: string }[];
  onRunStoryCraft: () => void;
  onDeleteStoryCraft: () => void;
  onToggleChecklistItem: (itemId: string, completed: boolean) => void;
  onUpdateChecklistItem: (itemId: string, patch: { category?: string; suggestion?: string }) => void;
  onAddChecklistItem: () => void;
  onDeleteChecklistItem: (itemId: string) => void;
  onOpenManuscript: () => void;
  onGenerateVariationFromChecklist?: () => void;
  variationBusy?: boolean;
  showEditorScore?: boolean;
  editorFeedback?: DesktopEditorFeedback | null;
  canEditEditorScore?: boolean;
  onSaveEditorScore?: (next: {
    assessment: import("../../../shared/storyCraftFeedback").StoryCraftAssessment;
    summary: string | null;
    average: number | null;
  }) => Promise<void> | void;
}) {
  const hasFeedback = Boolean(feedback);
  const analyzing = feedback?.summary === "Analyzing…";
  const failed = Boolean(feedback?.summary?.startsWith("Analysis failed"));
  const avg = feedback ? averageStoryCraftScore(feedback) : null;
  const completedCount = feedback?.checklist.filter((c) => c.isCompleted).length ?? 0;
  const totalCount = feedback?.checklist.length ?? 0;
  const openChecklistCount =
    feedback?.checklist.filter((c) => !c.isCompleted && c.suggestion.trim().length > 0).length ?? 0;
  const assessment = feedback?.assessment;
  const weakAreas = useMemo(() => {
    if (!assessment) return [] as string[];
    return ASSESSMENT_ROWS.filter(({ key }) => {
      const score = assessment[key]?.score;
      return typeof score === "number" && score > 0 && score < 3.5;
    }).map(({ key }) => key);
  }, [assessment]);
  const [learnToken, setLearnToken] = useState<string | null>(null);
  const resolvedEditorFeedback =
    editorFeedback ?? editorFeedbackFromChapterFields(activeChapter.metadata?.customFields);
  const showEditor = showEditorScore || resolvedEditorFeedback != null || canEditEditorScore;
  // Always land on Overview: with no analysis it carries the "Run analysis"
  // call to action, which is the next step the user needs.
  const defaultTab = "overview";
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    setTab(defaultTab);
  }, [activeChapter.id, defaultTab]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!cloudAuthDesktopV2.isSignedIn()) {
        if (!cancelled) setLearnToken(null);
        return;
      }
      const token = await cloudAuthDesktopV2.getValidAccessToken().catch(() => null);
      if (!cancelled) setLearnToken(token);
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const snapshot = feedback ? displayedSnapshot(feedback) : null;

  return (
    <WorkbenchPage>
      <WorkbenchPageHeader
        title="Story Craft"
        description={`Chapter ${activeChapter.order}: ${activeChapter.title}`}
        actions={
          <>
            {busy || analyzing ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                <Spinner size={12} aria-label="Analyzing" />
                Analyzing…
              </span>
            ) : null}
            {hasFeedback && !analyzing && avg != null && avg > 0 ? (
              <ScoreBadge score={avg} label={scoreLabel(avg)} />
            ) : null}
            {totalCount > 0 ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                {completedCount}/{totalCount} tasks
              </span>
            ) : null}
            <LlmModelSelect
              value={storyCraftModel}
              onChange={onStoryCraftModelChange}
              openAiModels={chatModelsOpenAi}
              anthropicModels={chatModelsAnthropic}
              title="Book analysis model"
              aria-label="Book analysis model"
              disabled={busy}
            />
            <Button size="sm" type="button" onClick={onRunStoryCraft} disabled={busy}>
              {hasFeedback && !analyzing ? "Re-run" : "Run analysis"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" type="button" variant="outline" disabled={busy}>
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onOpenManuscript}>Go to manuscript</DropdownMenuItem>
                {hasFeedback && !analyzing ? (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={onDeleteStoryCraft}
                    disabled={busy}
                  >
                    Delete analysis
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1">
        <WorkbenchPageToolbar>
          <TabsList variant="line" aria-label="Story Craft sections">
            <TabsTrigger variant="line" value="overview">
              Overview
            </TabsTrigger>
            <TabsTrigger variant="line" value="scores">
              Scores
            </TabsTrigger>
            {showEditor ? (
              <TabsTrigger variant="line" value="editor">
                Editor
              </TabsTrigger>
            ) : null}
            <TabsTrigger variant="line" value="checklist">
              Checklist
              <TabsCount>{totalCount}</TabsCount>
            </TabsTrigger>
            <TabsTrigger variant="line" value="learn">
              Learn
            </TabsTrigger>
            <TabsTrigger variant="line" value="promises">
              Promises
              <TabsCount>{feedback?.promisesMade.length ?? 0}</TabsCount>
            </TabsTrigger>
            <TabsTrigger variant="line" value="seeds">
              Seeds
              <TabsCount>{feedback?.seeds.length ?? 0}</TabsCount>
            </TabsTrigger>
            <TabsTrigger variant="line" value="payoffs">
              Payoffs
              <TabsCount>{feedback?.promisesKept.length ?? 0}</TabsCount>
            </TabsTrigger>
          </TabsList>
        </WorkbenchPageToolbar>

        <WorkbenchPageContent>
          <TabsContent value="overview">
            {!hasFeedback ? (
              <EmptyState
                title="No Story Craft analysis yet"
                description="Run analysis to get a story snapshot, scores, checklist, and narrative tracking."
                action={
                  <Button size="sm" type="button" onClick={onRunStoryCraft} disabled={busy}>
                    Run analysis
                  </Button>
                }
              />
            ) : failed ? (
              <SectionCard tone="muted" title="Analysis failed" rail>
                <p className="text-sm leading-relaxed text-destructive">{feedback?.summary}</p>
              </SectionCard>
            ) : analyzing ? (
              <EmptyState
                title="Working…"
                description="Story Craft is analyzing this chapter. You can keep editing other tabs."
              />
            ) : (
              <div className="mx-auto flex max-w-208 flex-col gap-3">
                <SectionCard title="Story snapshot">
                  {snapshot ? (
                    <p className="m-0 max-w-[68ch] whitespace-pre-wrap text-sm leading-relaxed text-foreground">{snapshot}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No story snapshot available. Re-run analysis to generate one.
                    </p>
                  )}
                </SectionCard>
                {assessment?.overallNotes?.trim() ? (
                  <SectionCard title="Overall notes">
                    <p className="m-0 max-w-[68ch] whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {assessment.overallNotes}
                    </p>
                  </SectionCard>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" type="button" variant="outline" onClick={() => setTab("scores")}>
                    View scores
                  </Button>
                  <Button size="sm" type="button" variant="outline" onClick={() => setTab("checklist")}>
                    Open checklist
                  </Button>
                  <Button size="sm" type="button" variant="ghost" onClick={onOpenManuscript}>
                    Go to manuscript →
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="scores">
            {!assessment || !hasFeedback || analyzing || failed ? (
              <EmptyState
                title="No scores yet"
                description="Run Story Craft analysis to score plot, character, theme, pacing, conflict, and hook."
                action={
                  <Button size="sm" type="button" onClick={onRunStoryCraft} disabled={busy}>
                    Run analysis
                  </Button>
                }
              />
            ) : (
              <div className="flex flex-col gap-3">
                <div className="grid items-start gap-3 grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))]">
                  {ASSESSMENT_ROWS.map(({ key, label }) => {
                    const data = assessment[key];
                    return (
                      <ScoreCard
                        key={key}
                        label={label}
                        score={data.score}
                        notes={data.notes || undefined}
                        footer={<ImproveThisStrip area={key as StoryCraftAreaId} />}
                      />
                    );
                  })}
                </div>
                {assessment.overallNotes?.trim() ? (
                  <SectionCard title="Overall notes">
                    <p className="m-0 max-w-[68ch] whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {assessment.overallNotes}
                    </p>
                  </SectionCard>
                ) : null}
              </div>
            )}
          </TabsContent>

          <TabsContent value="learn">
            <LearnPanel
              baseUrl={getWebApiBaseUrl()}
              authToken={learnToken}
              weakAreas={weakAreas}
            />
          </TabsContent>

          {showEditor ? (
            <TabsContent value="editor">
              {onSaveEditorScore ? (
                <EditorScoreForm
                  initial={resolvedEditorFeedback}
                  canEdit={canEditEditorScore && (activeChapter.kind ?? "chapter") === "chapter"}
                  busy={busy}
                  onSave={onSaveEditorScore}
                />
              ) : (
                <EmptyState title="Editor scoring unavailable" description="This chapter cannot be scored manually." />
              )}
            </TabsContent>
          ) : null}

          <TabsContent value="checklist">
            <div className="mx-auto flex max-w-208 flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="m-0 text-sm font-semibold text-foreground">Improvement checklist</h3>
                <div className="flex flex-wrap gap-1.5">
                  <Button type="button" size="sm" variant="outline" onClick={onAddChecklistItem} disabled={busy}>
                    Add item
                  </Button>
                  {onGenerateVariationFromChecklist ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={onGenerateVariationFromChecklist}
                      disabled={busy || variationBusy || openChecklistCount === 0}
                      aria-busy={variationBusy}
                    >
                      {variationBusy ? "Generating…" : "Generate variation"}
                    </Button>
                  ) : null}
                </div>
              </div>
              {feedback && feedback.checklist.length > 0 ? (
                <div className="divide-y divide-border/50 overflow-hidden rounded-md border border-border/70 bg-card">
                  {feedback.checklist.map((checkItem) => (
                    <StoryCraftChecklistItemRow
                      key={checkItem.id}
                      item={checkItem}
                      busy={busy}
                      onToggle={(completed) => onToggleChecklistItem(checkItem.id, completed)}
                      onUpdate={(patch) => onUpdateChecklistItem(checkItem.id, patch)}
                      onDelete={() => onDeleteChecklistItem(checkItem.id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  compact
                  title="No checklist items yet"
                  description="Add one manually or re-run analysis to generate improvement tasks."
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="promises">
            {feedback && feedback.promisesMade.length > 0 ? (
              <div className="mx-auto flex max-w-208 flex-col gap-3">
                {feedback.promisesMade.map((promise) => (
                  <SectionCard
                    key={promise.id}
                    title={
                      <Badge
                        variant="outline"
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[0.625rem] font-bold tracking-wide uppercase",
                          promiseTypeTone(promise.type)
                        )}
                      >
                        {promise.type}
                      </Badge>
                    }
                  >
                    <p className="m-0 max-w-[68ch] text-sm leading-relaxed text-foreground">{promise.description}</p>
                    {promise.context ? (
                      <blockquote className="mt-2.5 max-w-[68ch] border-l-2 border-border/90 pl-3 text-sm italic leading-relaxed text-muted-foreground">{promise.context}</blockquote>
                    ) : null}
                  </SectionCard>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No promises identified"
                description="Narrative setups and foreshadowing from this chapter will appear here after analysis."
              />
            )}
          </TabsContent>

          <TabsContent value="seeds">
            {feedback && feedback.seeds.length > 0 ? (
              <div className="mx-auto flex max-w-208 flex-col gap-3">
                {feedback.seeds.map((seed) => (
                  <SectionCard
                    key={seed.id}
                    title="Planted seed"
                    actions={
                      seed.payoffChapterTitle ? (
                        <span className="text-xs text-muted-foreground">Pays off in {seed.payoffChapterTitle}</span>
                      ) : null
                    }
                  >
                    <p className="m-0 max-w-[68ch] text-sm leading-relaxed text-foreground">{seed.description}</p>
                    {seed.context ? <blockquote className="mt-2.5 max-w-[68ch] border-l-2 border-border/90 pl-3 text-sm italic leading-relaxed text-muted-foreground">{seed.context}</blockquote> : null}
                    <div className="mt-3">
                      <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">Payoff</span>
                      <p className="m-0 mt-0.5 max-w-[68ch] text-sm leading-relaxed text-foreground/85">{seed.payoffDescription}</p>
                    </div>
                  </SectionCard>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No seeds with later payoffs"
                description="Re-run with a high-tier model to scan the full manuscript for planted seeds."
              />
            )}
          </TabsContent>

          <TabsContent value="payoffs">
            {feedback && feedback.promisesKept.length > 0 ? (
              <div className="mx-auto flex max-w-208 flex-col gap-3">
                {feedback.promisesKept.map((kept, keptIndex) => (
                  <SectionCard
                    key={kept.promiseId || `kept-${keptIndex}`}
                    title="Resolved promise"
                    actions={
                      kept.chapterTitleWherePromised ? (
                        <span className="text-xs text-muted-foreground">Promised in {kept.chapterTitleWherePromised}</span>
                      ) : null
                    }
                  >
                    <div className="mt-3">
                      <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">Promise</span>
                      <p className="m-0 mt-0.5 max-w-[68ch] text-sm leading-relaxed text-foreground/85">{kept.promiseDescription}</p>
                    </div>
                    <div className="mt-3">
                      <span className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">Resolution</span>
                      <p className="m-0 mt-0.5 max-w-[68ch] text-sm leading-relaxed text-foreground/85">{kept.howKept}</p>
                    </div>
                  </SectionCard>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No payoffs in this chapter"
                description="Resolved promises from earlier chapters will appear here."
              />
            )}
          </TabsContent>
        </WorkbenchPageContent>
      </Tabs>
    </WorkbenchPage>
  );
}
